import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Avatar, Box, Button, Chip, IconButton, Paper, Stack, TextField, Typography } from '@mui/material'
import { AttachFile, CameraAlt, Check, Description, Image, Payments, Send, Verified } from '@mui/icons-material'
import api from '../services/api'

export default function ServiceProJobWorkspace({ requestId }) {
  const [data, setData] = useState({ messages: [] })
  const [body, setBody] = useState('')
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(null)
  const picker = useRef()

  const load = useCallback(async (silent = false) => {
    if (!silent) setBusy(true)
    try {
      const response = await api.get(`/service-pros/requests/${requestId}/conversation`)
      setData(response.data.data || { messages: [] })
      setError('')
    } catch (requestError) {
      if (!silent) setError(requestError.response?.data?.message || 'Job communication could not be loaded.')
    } finally {
      if (!silent) setBusy(false)
    }
  }, [requestId])

  useEffect(() => {
    load()
    const timer = window.setInterval(() => load(true), 3500)
    return () => window.clearInterval(timer)
  }, [load])

  const send = async () => {
    if (!body.trim() && !file) return
    setBusy(true)
    try {
      const payload = { body: body.trim() }
      if (file) {
        payload.attachment_data = await dataUrl(file)
        payload.attachment_name = file.name
        payload.attachment_mime = file.type || 'application/octet-stream'
        payload.message_type = file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : file.type.startsWith('audio/') ? 'voice' : 'document'
      }
      await api.post(`/service-pros/requests/${requestId}/conversation/messages`, payload)
      setBody('')
      setFile(null)
      await load(true)
    } catch (requestError) {
      setError(apiError(requestError))
    } finally {
      setBusy(false)
    }
  }

  const progress = async (status, note) => {
    setBusy(true)
    try {
      const response = await api.post(`/service-pros/requests/${requestId}/job/progress`, { status, note })
      setSuccess({ title: response.data.title || 'Progress Update Posted', message: response.data.message || 'Your customer has been notified successfully.' })
      setError('')
      await load(true)
    } catch (requestError) {
      setError(apiError(requestError))
    } finally {
      setBusy(false)
    }
  }

  const confirm = async () => {
    setBusy(true)
    try {
      await api.post(`/service-pros/requests/${requestId}/job/confirm-completion`)
      await load(true)
    } catch (requestError) {
      setError(apiError(requestError))
    } finally {
      setBusy(false)
    }
  }

  const download = async (message) => {
    try {
      const response = await api.get(`/service-pros/requests/${requestId}/conversation/messages/${message.id}/attachment`, { responseType: 'blob' })
      const url = URL.createObjectURL(response.data)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = message.attachment_name || 'attachment'
      anchor.click()
      URL.revokeObjectURL(url)
    } catch (_) {
      setError('The secure attachment could not be downloaded.')
    }
  }

  const provider = data.provider || {}
  const job = data.job || {}
  const role = data.viewer_role
  const messages = data.messages || []

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 2, borderRadius: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar src={provider.avatar_url || ''} sx={{ width: 60, height: 60 }}>{provider.business_name?.slice(0, 2)}</Avatar>
          <Box flex={1}>
            <Stack direction="row" spacing={0.6} alignItems="center">
              <Typography variant="h6" fontWeight={900}>{provider.business_name || 'Service Pro'}</Typography>
              {provider.verified && <Verified color="primary" />}
            </Stack>
            <Typography color={provider.online ? 'success.main' : 'text.secondary'}>{provider.online ? 'Online now' : 'Offline'} · {pretty(job.status)}</Typography>
          </Box>
          <Chip label={pretty(job.status || 'active')} color={job.status === 'completed' ? 'success' : 'primary'} />
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={900} mb={1.5}>Job summary</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
          {[
            ['Customer name', job.customer_name], ['Job ID', job.id], ['Service', job.service],
            ['Address', job.address || job.location || 'Not provided'],
            ['Scheduled date & time', job.schedule_label || 'To be agreed in chat'], ['Current status', pretty(job.status)],
            ['Payment status', pretty(job.payment_status)],
          ].map(([label, value]) => <Summary key={label} label={label} value={value} />)}
        </Box>
      </Paper>

      {role === 'provider' && <Paper sx={{ p: 2, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={900} mb={1.5}>Payment summary</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
          {[
            ['Total estimate', cash(job.currency, job.quote_amount)], ['Deposit received', cash(job.currency, job.deposit_received)],
            ['Amount paid', cash(job.currency, job.paid_amount)], ['Remaining balance', cash(job.currency, job.remaining_balance)],
            ['Outstanding balance', cash(job.currency, job.outstanding_balance)], ['Payment status', pretty(job.payment_status)],
            ['Final payment', job.final_payment_released ? 'Released' : 'Not Released'],
          ].map(([label, value]) => <Summary key={label} label={label} value={value} />)}
        </Box>
      </Paper>}

      <Alert severity="warning"><strong>Pay only through MojoPayr.</strong> Outside payments are at your own risk.</Alert>
      {role === 'provider' && success && <Alert severity="success" icon={<Check />} onClose={() => setSuccess(null)}><Typography fontWeight={900}>✓ {success.title}</Typography><Typography>{success.message}</Typography></Alert>}
      {error && <Alert severity="error">{error}</Alert>}

      <Paper sx={{ p: 2, borderRadius: 3 }}>
        <Typography fontWeight={900} mb={1}>Quick actions</Typography>
        <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
          <Button variant="outlined" onClick={() => window.alert(`${job.service || 'Service'}\n${cash(job.currency, job.quote_amount)}\n${job.schedule_label || 'Schedule to be agreed in MojoPayr chat.'}`)}>View Quote</Button>
          <Button variant="outlined" onClick={() => window.alert(`Quote: ${cash(job.currency, job.quote_amount)}\nPaid: ${cash(job.currency, job.paid_amount)}\nRemaining: ${cash(job.currency, job.remaining_balance)}\n\nMojoPayr receipts remain in transaction history.`)}>View Invoice</Button>
          {role === 'customer' && Number(job.remaining_balance) > 0 && <Button variant="contained" startIcon={<Payments />} href="#service-pro-payments">Pay Now</Button>}
          <Button variant="outlined" startIcon={<Image />} onClick={() => picker.current?.click()}>Upload Photos</Button>
          <Button variant="outlined" startIcon={<Description />} onClick={() => picker.current?.click()}>Upload Documents</Button>
          {role === 'provider' && <>
            <Button variant="outlined" onClick={() => progress('provider_en_route', 'Provider is on the way to the service location.')}>Mark En Route</Button>
            <Button variant="outlined" onClick={() => progress('progress', 'Provider arrived at the service location.')}>Mark Arrival</Button>
            <Button variant="outlined" onClick={() => progress('work_started', 'Work started.')}>Start Work</Button>
            <Button variant="outlined" onClick={() => progress('work_paused', 'Work paused.')}>Pause Work</Button>
            <Button variant="outlined" onClick={() => progress('work_completed', 'Work completed and ready for confirmation.')}>Complete Work</Button>
          </>}
          {role === 'customer' && job.status === 'awaiting_customer_confirmation' && <Button color="success" variant="contained" onClick={confirm}>Confirm Completion</Button>}
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, borderRadius: 3 }}>
        <Typography fontWeight={900} mb={1}>Job timeline</Typography>
        {providerTimeline(data.timeline || [], job).map((event) => <Stack key={event.key} direction="row" spacing={1.2} pb={1.2}><Box sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: event.complete ? '#32c766' : 'rgba(255,255,255,.25)', mt: 0.7 }} /><Box><Typography fontWeight={800} color={event.complete ? 'text.primary' : 'text.secondary'}>{event.complete ? '✓' : '○'} {event.label}</Typography><Typography variant="caption" color="text.secondary">{event.at ? new Date(event.at).toLocaleString() : 'Pending'}</Typography></Box></Stack>)}
      </Paper>

      <Paper sx={{ p: 2, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={900}>Job communication</Typography>
        <Box sx={{ height: 480, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
          {messages.length === 0 && <Typography color="text.secondary" m="auto">Start the secure job conversation here.</Typography>}
          {messages.map((message) => <Message key={message.id} message={message} onDownload={() => download(message)} />)}
        </Box>
        {file && <Chip label={file.name} onDelete={() => setFile(null)} sx={{ mb: 1 }} />}
        <Stack direction="row" alignItems="flex-end" spacing={0.5} sx={{ p: 1, border: '1px solid rgba(255,255,255,.14)', borderRadius: 3 }}>
          <IconButton onClick={() => picker.current?.click()}><CameraAlt /></IconButton><IconButton onClick={() => picker.current?.click()}><AttachFile /></IconButton>
          <TextField fullWidth multiline maxRows={5} placeholder="Message about this job…" value={body} onChange={(event) => setBody(event.target.value)} variant="standard" InputProps={{ disableUnderline: true }} />
          <IconButton color="primary" onClick={send} disabled={busy || (!body.trim() && !file)}><Send /></IconButton>
          <input ref={picker} hidden type="file" accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx" onChange={(event) => setFile(event.target.files?.[0] || null)} />
        </Stack>
      </Paper>
    </Stack>
  )
}

function Summary({ label, value }) { return <Box sx={{ p: 1.2, borderRadius: 2, bgcolor: 'rgba(255,255,255,.04)' }}><Typography variant="caption" color="text.secondary">{label}</Typography><Typography fontWeight={800}>{value || '—'}</Typography></Box> }
function Message({ message, onDownload }) { return <Box sx={{ alignSelf: message.mine ? 'flex-end' : 'flex-start', maxWidth: '82%', p: 1.4, borderRadius: message.mine ? '18px 18px 4px 18px' : '18px 18px 18px 4px', bgcolor: message.mine ? 'rgba(255,138,0,.2)' : 'rgba(255,255,255,.07)' }}>{message.body && <Typography>{message.body}</Typography>}{message.attachment_url && <Button startIcon={<AttachFile />} onClick={onDownload}>{message.attachment_name || 'Secure attachment'}</Button>}<Stack direction="row" justifyContent="flex-end" spacing={0.4}><Typography variant="caption" color="text.secondary">{new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Typography>{message.mine && <Check sx={{ fontSize: 14, color: message.read_at ? 'success.main' : 'text.secondary' }} />}</Stack></Box> }
const pretty = (value) => String(value || '').replaceAll('_', ' ').replace(/\b\w/g, (character) => character.toUpperCase())
const cash = (currency, value) => `${currency || ''} ${Number(value || 0).toFixed(2)}`.trim()
const dataUrl = (file) => new Promise((resolve, reject) => { const reader = new FileReader(); reader.onload = () => resolve(reader.result); reader.onerror = reject; reader.readAsDataURL(file) })
const apiError = (error) => { const errors = error.response?.data?.errors; const first = errors && Object.values(errors)[0]; return first?.[0] || error.response?.data?.message || 'This action could not be completed.' }
const providerTimeline = (events, job) => {
  const found = new Map(events.map((event) => [event.key, event]))
  const has = (...keys) => keys.some((key) => found.has(key))
  const status = String(job.status || '')
  return [
    ['request_received', 'Request Received', has('request_received', 'request_submitted')],
    ['estimate_sent', 'Estimate Sent', has('estimate_sent', 'quote_sent')],
    ['customer_accepted', 'Customer Accepted', has('customer_accepted', 'quote_accepted')],
    ['deposit_paid', 'Deposit Paid', has('deposit_paid') || Number(job.deposit_received) > 0],
    ['work_started', 'Work Started', has('work_started', 'started')],
    ['work_completed', 'Work Completed', has('work_completed')],
    ['customer_approval', 'Waiting for Customer Approval', status === 'awaiting_customer_confirmation'],
    ['final_payment_pending', 'Final Payment Pending', Number(job.remaining_balance) > 0 && Number(job.paid_amount) > 0],
    ['final_payment_released', 'Final Payment Released', Boolean(job.final_payment_released)],
  ].map(([key, label, complete]) => ({ key, label, complete, at: found.get(key)?.at }))
}
